import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SubscriptionPreferencesPanel = ({ isReadOnly = false }) => {
  const [preferences, setPreferences] = useState({
    autoRenewal: true,
    billingCycle: 'monthly',
    invoiceDelivery: 'email',
    paymentReminder: 7,
    scheduledChange: null,
    prorationEnabled: true
  });
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    action: 'upgrade',
    targetTier: 'tier_10_50',
    effectiveDate: ''
  });

  const billingCycleOptions = [
    { value: 'monthly', label: 'Monthly', description: 'Billed every month' },
    { value: 'annual', label: 'Annual', description: 'Billed once per year (save 15%)' }
  ];

  const invoiceDeliveryOptions = [
    { value: 'email', label: 'Email', description: 'Send to billing contacts' },
    { value: 'portal', label: 'Portal Only', description: 'Available in dashboard' },
    { value: 'both', label: 'Email & Portal', description: 'Both methods' }
  ];

  const reminderOptions = [
    { value: 3, label: '3 days before' },
    { value: 7, label: '7 days before' },
    { value: 14, label: '14 days before' },
    { value: 30, label: '30 days before' }
  ];

  const tierOptions = [
    { value: 'tier_1_10', label: '1-10 Employees (K500/mo)' },
    { value: 'tier_11_50', label: '11-50 Employees (K1,500/mo)' },
    { value: 'tier_51_500', label: '51-500 Employees (K4,500/mo)' },
    { value: 'tier_501_plus', label: '501+ Employees (K13,500/mo)' }
  ];

  const handleTogglePreference = (key) => {
    if (isReadOnly) return;
    setPreferences({ ...preferences, [key]: !preferences?.[key] });
  };

  const handleScheduleChange = () => {
    if (isReadOnly) return;
    if (scheduleData?.targetTier && scheduleData?.effectiveDate) {
      setPreferences({
        ...preferences,
        scheduledChange: {
          ...scheduleData,
          scheduledAt: new Date()?.toISOString()
        }
      });
      setShowScheduleForm(false);
      setScheduleData({ action: 'upgrade', targetTier: 'tier_10_50', effectiveDate: '' });
    }
  };

  const handleCancelScheduledChange = () => {
    if (isReadOnly) return;
    setPreferences({ ...preferences, scheduledChange: null });
  };

  const calculateProration = () => {
    if (!preferences?.scheduledChange) return null;
    const currentPrice = 500;
    const newPrice = 1000;
    const daysRemaining = 15;
    const proration = ((newPrice - currentPrice) / 30) * daysRemaining;
    return proration?.toFixed(2);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Subscription Preferences</h2>
        <p className="text-sm text-muted-foreground mt-1">Configure billing and subscription settings</p>
      </div>

      {/* Auto-Renewal */}
      <div className="border border-border rounded-lg p-4 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded">
              <Icon name="RefreshCw" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">Auto-Renewal</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically renew subscription at the end of billing period
              </p>
            </div>
          </div>
          <button
            onClick={() => handleTogglePreference('autoRenewal')}
            disabled={isReadOnly}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              preferences?.autoRenewal ? 'bg-primary' : 'bg-muted'
            } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                preferences?.autoRenewal ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Billing Cycle */}
      <div className="border border-border rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 bg-secondary/10 rounded">
            <Icon name="Calendar" size={20} className="text-secondary" />
          </div>
          <div>
            <h3 className="font-medium text-foreground">Billing Cycle</h3>
            <p className="text-sm text-muted-foreground mt-1">Choose how often you want to be billed</p>
          </div>
        </div>
        <Select
          value={preferences?.billingCycle}
          onChange={(value) => !isReadOnly && setPreferences({ ...preferences, billingCycle: value })}
          options={billingCycleOptions}
          disabled={isReadOnly}
        />
        {preferences?.billingCycle === 'annual' && (
          <div className="mt-2 p-2 bg-success/10 border border-success/20 rounded flex items-center gap-2">
            <Icon name="Tag" size={14} className="text-success" />
            <span className="text-xs text-success">Save 15% with annual billing</span>
          </div>
        )}
      </div>

      {/* Invoice Delivery */}
      <div className="border border-border rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 bg-accent/10 rounded">
            <Icon name="FileText" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="font-medium text-foreground">Invoice Delivery</h3>
            <p className="text-sm text-muted-foreground mt-1">How you want to receive invoices</p>
          </div>
        </div>
        <Select
          value={preferences?.invoiceDelivery}
          onChange={(value) => !isReadOnly && setPreferences({ ...preferences, invoiceDelivery: value })}
          options={invoiceDeliveryOptions}
          disabled={isReadOnly}
        />
      </div>

      {/* Payment Reminder */}
      <div className="border border-border rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 bg-warning/10 rounded">
            <Icon name="Bell" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="font-medium text-foreground">Payment Reminder</h3>
            <p className="text-sm text-muted-foreground mt-1">When to send payment reminders</p>
          </div>
        </div>
        <Select
          value={preferences?.paymentReminder}
          onChange={(value) => !isReadOnly && setPreferences({ ...preferences, paymentReminder: value })}
          options={reminderOptions}
          disabled={isReadOnly}
        />
      </div>

      {/* Proration */}
      <div className="border border-border rounded-lg p-4 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded">
              <Icon name="Calculator" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">Prorated Billing</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Calculate charges based on remaining billing period
              </p>
            </div>
          </div>
          <button
            onClick={() => handleTogglePreference('prorationEnabled')}
            disabled={isReadOnly}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              preferences?.prorationEnabled ? 'bg-primary' : 'bg-muted'
            } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                preferences?.prorationEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Scheduled Change */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 bg-secondary/10 rounded">
            <Icon name="TrendingUp" size={20} className="text-secondary" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-foreground">Scheduled Tier Change</h3>
            <p className="text-sm text-muted-foreground mt-1">Schedule upgrade or downgrade</p>
          </div>
        </div>

        {preferences?.scheduledChange ? (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {preferences?.scheduledChange?.action?.charAt(0)?.toUpperCase() + preferences?.scheduledChange?.action?.slice(1)} scheduled
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Effective: {new Date(preferences?.scheduledChange?.effectiveDate)?.toLocaleDateString()}
                </p>
              </div>
              <Button
                size="xs"
                variant="ghost"
                iconName="X"
                onClick={handleCancelScheduledChange}
              />
            </div>
            {preferences?.prorationEnabled && calculateProration() && (
              <div className="mt-2 pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Prorated charge: <span className="font-medium text-foreground">K{calculateProration()}</span>
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {!showScheduleForm ? (
              <Button
                size="sm"
                variant="outline"
                iconName="Plus"
                onClick={() => setShowScheduleForm(true)}
                fullWidth
              >
                Schedule Change
              </Button>
            ) : (
              <div className="space-y-3">
                <Select
                  label="Action"
                  value={scheduleData?.action}
                  onChange={(value) => setScheduleData({ ...scheduleData, action: value })}
                  options={[
                    { value: 'upgrade', label: 'Upgrade Tier' },
                    { value: 'downgrade', label: 'Downgrade Tier' }
                  ]}
                />
                <Select
                  label="Target Tier"
                  value={scheduleData?.targetTier}
                  onChange={(value) => setScheduleData({ ...scheduleData, targetTier: value })}
                  options={tierOptions}
                />
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Effective Date</label>
                  <input
                    type="date"
                    value={scheduleData?.effectiveDate}
                    onChange={(e) => setScheduleData({ ...scheduleData, effectiveDate: e?.target?.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleScheduleChange}>Schedule</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowScheduleForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Security Notice */}
      <div className="mt-6 p-3 bg-muted/30 border border-border rounded-lg flex items-start gap-2">
        <Icon name="Shield" size={16} className="text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-foreground">Two-Factor Authentication</p>
          <p className="text-xs text-muted-foreground mt-1">
            Enable 2FA for additional security on billing changes
          </p>
        </div>
        <Button size="xs" variant="outline" className="ml-auto">Enable</Button>
      </div>
    </div>
  );
};

export default SubscriptionPreferencesPanel;